/**
 * Animation Controller - Advanced animation management for entities
 * 
 * Features:
 * - Animation state machine
 * - Blending between animations
 * - Animation events/callbacks
 * - Speed and weight control
 */

import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import type { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { Observer } from "@babylonjs/core/Misc/observable";

import { IDisposable } from "../core/Engine.types";

/**
 * Animation state
 */
export interface AnimationState {
    name: string;
    group: AnimationGroup;
    loop: boolean;
    speed: number;
    weight: number;
    blendInTime: number;
    blendOutTime: number;
}

/**
 * Animation transition
 */
export interface AnimationTransition {
    from: string;
    to: string;
    duration: number;
    condition?: () => boolean;
}

/**
 * Animation event callback
 */
export type AnimationEventCallback = (animationName: string) => void;

/**
 * AnimationController - Manages animations for an entity
 */
export class AnimationController implements IDisposable {
    private _animationGroups: Map<string, AnimationGroup> = new Map();
    private _states: Map<string, AnimationState> = new Map();
    private _currentState: AnimationState | null = null;
    private _previousState: AnimationState | null = null;
    private _transitions: AnimationTransition[] = [];
    private _defaultSpeed: number = 1.0;
    private _observers: Map<string, Observer<AnimationGroup>[]> = new Map();

    // Event callbacks
    private _onAnimationStart: AnimationEventCallback[] = [];
    private _onAnimationEnd: AnimationEventCallback[] = [];
    private _onAnimationLoop: AnimationEventCallback[] = [];

    /**
     * Get current animation name
     */
    public get currentAnimation(): string | null {
        return this._currentState?.name || null;
    }

    /**
     * Check if any animation is playing
     */
    public get isPlaying(): boolean {
        return this._currentState?.group.isPlaying ?? false;
    }

    /**
     * Get all animation names
     */
    public get animationNames(): string[] {
        return Array.from(this._animationGroups.keys());
    }

    constructor(animationGroups: AnimationGroup[], _skeleton: Skeleton | null = null) {
        // Skeleton stored for future bone-based animation features
        // Register all animation groups
        animationGroups.forEach(group => {
            // Use simplified name (remove common suffixes)
            let name = group.name
                .replace(/^.*\|/, "") // Remove prefix before |
                .replace(/_\d+$/, "") // Remove trailing numbers
                .toLowerCase();
            
            this._animationGroups.set(name, group);
            this._animationGroups.set(group.name, group); // Also store original name

            // Create default state
            this._states.set(name, {
                name,
                group,
                loop: true,
                speed: 1.0,
                weight: 1.0,
                blendInTime: 0.2,
                blendOutTime: 0.2,
            });
        });

        console.log(`AnimationController: Registered ${this._animationGroups.size} animations`);
    }

    /**
     * Configure an animation state
     */
    public configureState(
        name: string,
        options: Partial<Omit<AnimationState, "name" | "group">>
    ): void {
        const state = this._states.get(name.toLowerCase());
        if (!state) {
            console.warn(`AnimationController: State "${name}" not found`);
            return;
        }

        Object.assign(state, options);
    }

    /**
     * Add a transition between states
     */
    public addTransition(transition: AnimationTransition): void {
        this._transitions.push(transition);
    }

    /**
     * Play an animation by name
     */
    public play(
        name: string,
        options?: {
            loop?: boolean;
            speed?: number;
            blendTime?: number;
            from?: number;
            to?: number;
            onEnd?: () => void;
        }
    ): boolean {
        const normalizedName = name.toLowerCase();
        let group = this._animationGroups.get(normalizedName);
        
        // Try partial match if exact match not found
        if (!group) {
            for (const [key, ag] of this._animationGroups) {
                if (key.includes(normalizedName) || normalizedName.includes(key)) {
                    group = ag;
                    break;
                }
            }
        }

        if (!group) {
            console.warn(`AnimationController: Animation "${name}" not found`);
            console.log("Available:", this.animationNames);
            return false;
        }

        const state = this._states.get(normalizedName) || {
            name: normalizedName,
            group,
            loop: options?.loop ?? true,
            speed: options?.speed ?? this._defaultSpeed,
            weight: 1.0,
            blendInTime: options?.blendTime ?? 0.2,
            blendOutTime: 0.2,
        };

        // Handle transition from previous animation
        if (this._currentState && this._currentState.group !== group) {
            this._previousState = this._currentState;
            
            // Blend out previous
            if (state.blendOutTime > 0) {
                this._previousState.group.stop();
            } else {
                this._previousState.group.stop();
            }
        }

        // Update current state
        this._currentState = state;

        // Clear previous observers for this animation
        this._clearObservers(group.name);

        // Setup end callback
        if (options?.onEnd) {
            const observer = group.onAnimationGroupEndObservable.add(() => {
                options.onEnd!();
            });
            this._addObserver(group.name, observer);
        }

        // Play animation
        const loop = options?.loop ?? state.loop;
        const speed = options?.speed ?? state.speed;

        if (options?.from !== undefined && options?.to !== undefined) {
            group.start(loop, speed, options.from, options.to);
        } else {
            group.start(loop, speed);
        }

        // Emit event
        this._emitStart(state.name);

        // Setup loop/end events
        if (loop) {
            const loopObserver = group.onAnimationGroupLoopObservable.add(() => {
                this._emitLoop(state.name);
            });
            this._addObserver(group.name, loopObserver);
        } else {
            const endObserver = group.onAnimationGroupEndObservable.add(() => {
                this._emitEnd(state.name);
            });
            this._addObserver(group.name, endObserver);
        }

        return true;
    }

    /**
     * Stop current animation
     */
    public stop(): void {
        if (this._currentState) {
            this._currentState.group.stop();
            this._emitEnd(this._currentState.name);
            this._previousState = this._currentState;
            this._currentState = null;
        }
    }

    /**
     * Pause current animation
     */
    public pause(): void {
        if (this._currentState) {
            this._currentState.group.pause();
        }
    }

    /**
     * Resume paused animation
     */
    public resume(): void {
        if (this._currentState) {
            this._currentState.group.play();
        }
    }

    /**
     * Set animation speed
     */
    public setSpeed(speed: number): void {
        this._defaultSpeed = speed;
        if (this._currentState) {
            this._currentState.group.speedRatio = speed;
        }
    }

    /**
     * Get animation duration in seconds
     */
    public getAnimationDuration(name: string): number {
        const group = this._animationGroups.get(name.toLowerCase());
        if (!group) return 0;
        
        const fps = group.targetedAnimations[0]?.animation.framePerSecond || 30;
        return (group.to - group.from) / fps;
    }

    /**
     * Set animation weight for blending
     */
    public setWeight(name: string, weight: number): void {
        const group = this._animationGroups.get(name.toLowerCase());
        if (group) {
            group.setWeightForAllAnimatables(weight);
        }
    }

    /**
     * Subscribe to animation start events
     */
    public onStart(callback: AnimationEventCallback): void {
        this._onAnimationStart.push(callback);
    }

    /**
     * Subscribe to animation end events
     */
    public onEnd(callback: AnimationEventCallback): void {
        this._onAnimationEnd.push(callback);
    }

    /**
     * Subscribe to animation loop events
     */
    public onLoop(callback: AnimationEventCallback): void {
        this._onAnimationLoop.push(callback);
    }

    private _emitStart(name: string): void {
        this._onAnimationStart.forEach(cb => cb(name));
    }

    private _emitEnd(name: string): void {
        this._onAnimationEnd.forEach(cb => cb(name));
    }

    private _emitLoop(name: string): void {
        this._onAnimationLoop.forEach(cb => cb(name));
    }

    private _addObserver(groupName: string, observer: Observer<AnimationGroup> | null): void {
        if (!observer) return;
        if (!this._observers.has(groupName)) {
            this._observers.set(groupName, []);
        }
        this._observers.get(groupName)!.push(observer);
    }

    private _clearObservers(groupName: string): void {
        const group = this._animationGroups.get(groupName);
        const observers = this._observers.get(groupName);
        if (group && observers) {
            observers.forEach(obs => {
                group.onAnimationGroupEndObservable.remove(obs);
                group.onAnimationGroupLoopObservable.remove(obs);
            });
        }
        this._observers.delete(groupName);
    }

    /**
     * Dispose animation controller
     */
    public dispose(): void {
        this.stop();
        
        // Clear all observers
        this._observers.forEach((_observers, groupName) => {
            this._clearObservers(groupName);
        });

        this._animationGroups.clear();
        this._states.clear();
        this._transitions = [];
        this._onAnimationStart = [];
        this._onAnimationEnd = [];
        this._onAnimationLoop = [];
    }
}
